import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useCustomerView } from "@/context/CustomerViewContext";
import { formatApiErrorDetail } from "@/lib/errors";
import { Card, CardContent } from "@/components/ui/card";
import { toast } from "sonner";
import { Loader2, Package, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { OrderSheetDialog } from "@/components/OrderSheetDialog";
import { ORDER_STATUS, human } from "@/lib/statusLabels";

// §5 semantic badges: terracotta = action required, green = healthy, neutral = historical
export const ORDER_STATUS_BADGE = {
  CHECKOUT_HOLD: "badge-action",
  PAYMENT_PROCESSING: "badge-reserved",
  AWAITING_DEPOSIT: "badge-action",
  CONFIRMED: "badge-available",
  BALANCE_DUE: "badge-action",
  ADMIN_REVIEW: "badge-action",
  PAID: "badge-available",
  PARTIALLY_FULFILLED: "badge-available",
  FULFILLED: "badge-available",
  EXPIRED: "badge-neutral",
  FAILED: "badge-sold",
  CANCELLED: "badge-neutral",
  REFUNDED: "badge-neutral",
  REFUND_INITIATED: "badge-reserved",
  REFUND_FAILED: "badge-sold",
};

export default function DealerOrders() {
  const [rows, setRows] = useState(null);
  const [newOpen, setNewOpen] = useState(false);
  const navigate = useNavigate();
  const { customerView } = useCustomerView();

  useEffect(() => {
    setRows(null);
    api.get(`/orders${customerView ? "?customer_view=1" : ""}`).then((res) => setRows(res.data)).catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)));
  }, [customerView]);

  return (
    <div data-testid="dealer-orders-page">
      <p className="text-xs font-semibold uppercase tracking-[0.2em] text-terracotta">Purchasing</p>
      <div className="mt-3 flex flex-wrap items-end justify-between gap-4">
        <h1 className="font-display text-4xl font-light tracking-tight text-charcoal md:text-5xl">Orders</h1>
        {!customerView && (
          <Button onClick={() => setNewOpen(true)} data-testid="new-order-btn" className="h-11 bg-terracotta px-5 text-white hover:bg-terracotta-hover">
            <Plus className="mr-2 h-4 w-4" /> New order
          </Button>
        )}
      </div>
      <OrderSheetDialog open={newOpen} onOpenChange={setNewOpen} onCreated={(o) => { setNewOpen(false); navigate(`/dealer/orders/${o.id}`); }} />

      {!rows ? (
        <Loader2 className="mt-12 h-6 w-6 animate-spin text-terracotta" />
      ) : rows.length === 0 ? (
        <Card className="mt-12 border-border bg-white">
          <CardContent className="flex flex-col items-center p-16 text-center">
            <Package className="h-8 w-8 text-muted-foreground" />
            <p className="mt-4 text-sm text-muted-foreground" data-testid="orders-empty">No orders yet. Buy from the catalog or convert a reservation.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="mt-10 space-y-3" key={customerView ? "cv" : "trade"}>
          {rows.map((o) => (
            <Card key={o.id} className="cursor-pointer border-border bg-white transition-all duration-200 hover:-translate-y-[1px] hover:shadow-sm" data-testid={`order-card-${o.id}`} onClick={() => navigate(`/dealer/orders/${o.id}`)}>
              <CardContent className="flex flex-wrap items-center justify-between gap-4 p-5">
                <div>
                  <p className="font-medium text-charcoal" data-testid={`order-number-${o.id}`}>{o.order_number || "Order"}</p>
                  <p className="mt-0.5 text-xs text-muted-foreground">
                    {o.totals.boxes} boxes · {o.totals.sqft} sqft · {o.ship_to.kind === "direct_to_client" ? `client: ${o.ship_to.recipient_name}` : `ship to: ${o.ship_to.label || o.ship_to.address?.city || "showroom"}`}
                    {" · "}{new Date(o.created_at).toLocaleString()}
                  </p>
                </div>
                <div className="flex items-center gap-3">
                  <span className="num text-sm font-medium text-charcoal" data-testid={`order-total-${o.id}`}>
                    {customerView
                      ? (o.totals.retail_total != null ? `$${o.totals.retail_total.toFixed(2)}` : "—")
                      : o.payment?.captured_usd != null ? `$${o.payment.captured_usd.toFixed(2)} paid`
                        : (o.totals.total_usd != null ? `$${o.totals.total_usd.toFixed(2)}` : "—")}
                  </span>
                  <span className={`badge-status ${ORDER_STATUS_BADGE[o.status] || "badge-neutral"}`} data-testid={`order-status-${o.id}`}>{human(ORDER_STATUS, o.status)}</span>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
